import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import { Box, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, type MouseEvent, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { handleDownload } from "../../downloads/utils";
import { useArrivalState } from "../../hooks/useArrivalState";
import { useCanModifyUsers } from "../../hooks/useCanModifyUsers";
import { useIsMobile } from "../../hooks/useMobile";
import { useSyncEmployees } from "../../hooks/useSyncEmployees";
import { useSyncPtos } from "../../hooks/useSyncPtos";
import { useTeamRoster } from "../../hooks/useTeamRoster";
import { backendService } from "../../services/backend.service";
import { FONT_SIZE, INK } from "../../theme/tokens";
import { trackErrorEvent } from "../../utils/analytics";
import { hasHrisIntegrationOfType } from "../../utils/hrisIntegrations";
import { FROM_WAGE_BREAKDOWN_STATE_KEY, parseWageBreakdownOrigin } from "../../utils/navigation/wageBreakdownReturn";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { WageBreakdownReturnButton } from "../WageBreakdownReturn/WageBreakdownReturnButton";
import { ResendWelcomeDialog } from "./ResendWelcome/ResendWelcomeDialog";
import { getNeverLoggedInNurseIds } from "./useGetTeamStats";

export type TeamTabKey = "team" | "agency" | "admins" | "callInReasons" | "wageSettings" | "duplicates";
export type TeamTabItem = { key: TeamTabKey; label: string };

type TeamHeaderProps = {
	tabs: TeamTabItem[];
	activeTabKey: TeamTabKey;
	onTabChange: (key: TeamTabKey) => void;
	/** Optional "N employees waiting for decision" affordance, rendered in the right-side action group. */
	statusDecisionsBanner?: ReactNode;
	/**
	 * The employees Export should write, in the page's current view — the filtered rows, or the
	 * selected ones when there is a selection. Passed in because the filters live in `useTeamFilters`
	 * up in Team, and the export previously ignored them entirely: it always wrote every ACTIVE
	 * employee, so exporting an inactive-only view produced that view's exact complement.
	 */
	exportNurseIds: number[];
};

export const TeamHeader: FC<TeamHeaderProps> = ({ tabs, activeTabKey, onTabChange, statusDecisionsBanner, exportNurseIds }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { isMobile } = useIsMobile();
	const { chosenDepartment, refreshNursesData } = useHospitalData();
	const { roster } = useTeamRoster(chosenDepartment.department_id);
	const canModifyUsers = useCanModifyUsers();
	const neverLoggedInCount = useMemo(() => getNeverLoggedInNurseIds(roster).size, [roster]);
	const { mutateAsync: syncEmployees, isPending } = useSyncEmployees();
	const { mutateAsync: syncPtos, isPending: isSyncingPtos } = useSyncPtos();
	const hasEmployeesIntegration = hasHrisIntegrationOfType(chosenDepartment, "employees");
	const canSyncPtos = chosenDepartment?.hrisIntegrations?.some((i) => i.type === "time_off" && !i.file) ?? false;
	const canImport = hasEmployeesIntegration || canSyncPtos;
	// Which feed the roster comes from, for the panel's "last synced from X" line.
	const employeesIntegrationSource = chosenDepartment?.hrisIntegrations?.find((i) => i.type === "employees")?.source;

	// DAT-906 · Opened from a shift's wage breakdown: the way back to it.
	const breakdownOrigin = useArrivalState(FROM_WAGE_BREAKDOWN_STATE_KEY, parseWageBreakdownOrigin, null);
	const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
	const [isResendWelcomeOpen, setIsResendWelcomeOpen] = useState(false);
	const [isExporting, setIsExporting] = useState(false);

	const handleNavigateToAddNurse = useCallback((): void => {
		track("team_navigate_to_add_nurse", { department_id: chosenDepartment?.department_id });
		navigate(`/${chosenDepartment.department_id}/add-nurse`);
	}, [chosenDepartment?.department_id, navigate, track]);

	const handleImportEmployees = useCallback(async () => {
		if (!hasEmployeesIntegration) return;
		setOverflowAnchor(null);
		await syncEmployees({ startDate: DateTime.now().minus({ days: 1 }), endDate: DateTime.now() });
	}, [syncEmployees, hasEmployeesIntegration]);

	const handleImportPtos = useCallback(async () => {
		if (!canSyncPtos) return;
		setOverflowAnchor(null);
		await syncPtos();
	}, [syncPtos, canSyncPtos]);

	const handleExportTeam = useCallback(async () => {
		track("team_export_start", { department_id: chosenDepartment?.department_id, employee_count: exportNurseIds.length });
		setOverflowAnchor(null);
		setIsExporting(true);
		try {
			const response = await backendService.postTeamExport(chosenDepartment.department_id, exportNurseIds);
			const deptName = (chosenDepartment.name || "team").replace(/\s+/g, "_").toLowerCase();
			handleDownload(response.data, `${deptName}_team_export.xlsx`);
			track("team_export_success", { department_id: chosenDepartment?.department_id });
		} catch (error) {
			// Was a bare `catch {}` that only fired analytics: the spinner stopped and the user was told
			// nothing, so a failed export was indistinguishable from a browser that ate the download.
			trackErrorEvent("team_export_error", error, { department_id: chosenDepartment?.department_id });
			showSnackbar(t("export.file.generation.failed"), "error");
		} finally {
			setIsExporting(false);
		}
	}, [chosenDepartment?.department_id, chosenDepartment.name, exportNurseIds, showSnackbar, t, track]);

	const handleImportOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
		setOverflowAnchor(event.currentTarget);
	}, []);

	const handleOverflowClose = useCallback(() => setOverflowAnchor(null), []);

	const handleOpenResendWelcome = useCallback(() => {
		setOverflowAnchor(null);
		setIsResendWelcomeOpen(true);
	}, []);

	return (
		<Box
			component="header"
			sx={(theme) => ({
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: 2,
				flexWrap: "wrap",
				py: 1.5,
				px: { xs: 2, md: 3 },
				background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
				borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
			})}
		>
			{/* DAT-906 · Opened from a shift's wage breakdown: the way back leads the header, ahead of the tabs. */}
			<Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 2 }, minWidth: 0, maxWidth: "100%", flexWrap: "wrap" }}>
				{breakdownOrigin && (
					<>
						<WageBreakdownReturnButton origin={breakdownOrigin} />
						<Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
					</>
				)}
				<Box
					component="nav"
					aria-label={t("team.header.tabs_label")}
					sx={{
						display: "flex",
						// Centre, not baseline. The selected tab renders at 26px and the rest at 14px, so sharing a
						// baseline put the small tabs' centres 4.1px below the title's -- next to the title, the eye
						// reads that as the tab strip sagging. The header row and the button group both centre, so
						// this is the group that was aligned by a different rule from its two neighbours.
						alignItems: "center",
						columnGap: { xs: 1.5, md: 3 },
						rowGap: 0.5,
						flexWrap: "wrap",
						minWidth: 0,
						maxWidth: "100%",
					}}
				>
					{tabs.map((tab) => {
						const selected = tab.key === activeTabKey;
						return (
							<Box
								key={tab.key}
								component="button"
								type="button"
								role="tab"
								aria-selected={selected}
								data-testid={`team-tab-${tab.key}`}
								onClick={() => onTabChange(tab.key)}
								sx={(theme) => ({
									position: "relative",
									background: "none",
									border: "none",
									padding: 0,
									margin: 0,
									cursor: selected ? "default" : "pointer",
									fontFamily: "inherit",
									fontSize: selected ? { xs: "22px", md: "26px" } : "14px",
									fontWeight: selected ? 700 : 500,
									color: selected ? theme.palette.primary.main : INK.MUTED,
									letterSpacing: selected ? "-0.015em" : 0,
									lineHeight: 1.2,
									whiteSpace: "nowrap",
									transition: "color 180ms ease, transform 180ms ease",
									transformOrigin: "left center",
									// On mobile only the selected tab shows — the others are hidden so the page
									// reads as a single big title rather than a wrapped nav row.
									display: { xs: selected ? "inline-block" : "none", md: "inline-block" },
									// Animated underline for non-selected tabs: grows from the left on hover.
									"&::after": selected
										? undefined
										: {
												content: '""',
												position: "absolute",
												left: 0,
												bottom: -2,
												height: "2px",
												width: "100%",
												backgroundColor: theme.palette.primary.main,
												transform: "scaleX(0)",
												transformOrigin: "left center",
												transition: "transform 220ms ease",
												borderRadius: "2px",
											},
									"&:hover": selected
										? undefined
										: {
												color: theme.palette.primary.main,
												transform: "translateY(-1px)",
											},
									"&:hover::after": selected ? undefined : { transform: "scaleX(1)" },
									"&:focus-visible": {
										outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
										outlineOffset: 4,
										borderRadius: 2,
									},
								})}
							>
								{tab.label}
							</Box>
						);
					})}
				</Box>
			</Box>

			{activeTabKey === "team" && (
				<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
					{statusDecisionsBanner}
					{/* Everything without room for a button of its own lives in this menu, on every width.
					    Add Staff keeps its label: it is the one action people open this page to perform. */}
					<IconButton
						data-testid="team-header-overflow-button"
						onClick={handleImportOpen}
						aria-label={t("team.header.more_actions")}
						sx={(theme) => ({
							color: theme.palette.primary.main,
							border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
							borderRadius: "8px",
							width: 40,
							height: 40,
						})}
					>
						<MoreHorizIcon />
					</IconButton>
					{!isMobile && (
						<>
							{/* Import only exists when there is something to import FROM. Where it does, it opens a
							    panel that leads with when the roster last synced — the one-line answer to "is this
							    list current?" that previously required asking someone. */}
							{canImport && (
								<SystemButton
									data-testid="team-header-import-button"
									buttonVariant="secondary"
									onClick={handleImportOpen}
									leadingIcon={<SyncOutlinedIcon sx={{ fontSize: "18px" }} />}
									noFullWidthMobile
									sx={{ fontSize: "14px" }}
								>
									{t("team.header.import")}
								</SystemButton>
							)}
						</>
					)}

					<SystemButton
						data-testid="team-add-staff"
						buttonVariant="primary"
						onClick={handleNavigateToAddNurse}
						leadingIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: "18px" }} />}
						noFullWidthMobile
						sx={{ fontSize: "14px" }}
					>
						{t("team.header.add_staff")}
					</SystemButton>
				</Box>
			)}

			<Menu
				anchorEl={overflowAnchor}
				open={Boolean(overflowAnchor)}
				onClose={handleOverflowClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{ paper: { sx: { minWidth: 260, borderRadius: "8px", mt: 0.5 } } }}
			>
				{/* The "last synced from X" panel heads the menu only when there is an import to describe —
				    on a phone this same menu also carries Export, and a department with no HRIS would
				    otherwise get an Import heading over a menu that cannot import. */}
				{canImport && (
					<Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
						<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }}>{t("team.import.panel.title")}</Typography>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, mt: 0.25 }} data-testid="team-import-source">
							{employeesIntegrationSource ? t("team.import.panel.source", { source: employeesIntegrationSource }) : t("team.import.panel.no_source")}
						</Typography>
					</Box>
				)}
				{canImport && <Divider sx={{ my: 0.5 }} />}
				{hasEmployeesIntegration && (
					<MenuItem data-testid="team-header-import-employees-button" onClick={handleImportEmployees} disabled={isPending}>
						<ListItemIcon>
							<SyncOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={t("import.employees")} />
					</MenuItem>
				)}
				{canSyncPtos && (
					<MenuItem data-testid="team-header-import-ptos-button" onClick={handleImportPtos} disabled={isSyncingPtos}>
						<ListItemIcon>
							<SyncOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={t("team.sync.ptos.button")} />
					</MenuItem>
				)}
				{canImport && <Divider sx={{ my: 0.5 }} />}
				{/* An empty view would download an empty workbook, so there has to be something to export. */}
				<MenuItem data-testid="team-export" onClick={handleExportTeam} disabled={isExporting || exportNurseIds.length === 0}>
					<ListItemIcon>
						<FileDownloadOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={t("team.export")} />
				</MenuItem>
				{canModifyUsers && <Divider sx={{ my: 0.5 }} />}
				{canModifyUsers && (
					<MenuItem data-testid="team-header-resend-welcome" onClick={handleOpenResendWelcome}>
						<ListItemIcon>
							<MailOutlineOutlinedIcon fontSize="small" />
						</ListItemIcon>
						{/* The count is the reason to open it — how many people this action would actually reach. */}
						<ListItemText
							primary={t("team.resend_welcome.menu_item")}
							secondary={t("team.resend_welcome.menu_item_sub", { n: neverLoggedInCount })}
							slotProps={{ secondary: { sx: { fontSize: FONT_SIZE.LABEL_SM, color: INK.MUTED } } }}
						/>
					</MenuItem>
				)}
			</Menu>

			{/* Mounted only while open so every filter starts at its default — the dialog's own reset
			    contract, which a persistent instance would quietly outlive. */}
			{isResendWelcomeOpen && (
				<ResendWelcomeDialog
					open={isResendWelcomeOpen}
					onClose={() => setIsResendWelcomeOpen(false)}
					onSent={() => refreshNursesData(chosenDepartment.department_id)}
				/>
			)}
		</Box>
	);
};
